import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import * as React from "react";

import { cn } from "~/lib/cn";

interface CheckboxProps extends React.ComponentPropsWithoutRef<
  typeof CheckboxPrimitive.Root
> {
  children?: React.ReactNode;
  variant?: "default" | "success";
}

/** Checkbox with an inline label passed as children. */
const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(
  (
    { className, children, id, variant = "default", onCheckedChange, ...props },
    ref,
  ) => {
    const generatedId = React.useId();
    const checkboxId = id ?? generatedId;
    // Draw the tick only after a click, not for boxes that render already checked.
    const [drawTick, setDrawTick] = React.useState(false);

    const getVariantStyles = () => {
      if (variant === "success") {
        return {
          root: cn(
            "shrink-0 cursor-pointer rounded-full border-2 transition-colors duration-200",
            "flex items-center justify-center",
            "data-[state=checked]:border-green-600 data-[state=checked]:bg-green-600 data-[state=checked]:text-white",
            "data-[state=unchecked]:border-gray-300 data-[state=unchecked]:bg-transparent",
            "hover:data-[state=unchecked]:border-gray-400",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-70",
          ),
          indicator: "flex items-center justify-center text-white",
        };
      }

      // Default variant
      return {
        root: cn(
          "size-4 shrink-0 rounded-sm border transition-colors duration-200",
          "flex items-center justify-center",
          "data-[state=checked]:border-brand-700 data-[state=checked]:bg-brand-50 data-[state=checked]:text-brand-700",
          "data-[state=unchecked]:border-gray-200 data-[state=unchecked]:bg-transparent",
          "hover:data-[state=checked]:border-brand-500 hover:data-[state=checked]:bg-transparent hover:data-[state=checked]:text-brand-500",
          "hover:data-[state=unchecked]:border-brand-700 hover:data-[state=unchecked]:bg-brand-50",
          "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none",
          "disabled:cursor-not-allowed disabled:opacity-70",
        ),
        indicator: "flex items-center justify-center text-current",
      };
    };

    const styles = getVariantStyles();

    const checkbox = (
      <CheckboxPrimitive.Root
        ref={ref}
        id={checkboxId}
        data-slot="checkbox"
        className={cn(styles.root, className)}
        onCheckedChange={(checked) => {
          setDrawTick(true);
          onCheckedChange?.(checked);
        }}
        {...props}
      >
        <CheckboxPrimitive.Indicator
          data-slot="checkbox-indicator"
          className={styles.indicator}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.33317 2.5L3.74984 7.08333L1.6665 5"
              pathLength={1}
              className={cn(
                "[stroke-dasharray:1]",
                drawTick && "animate-check-draw",
              )}
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );

    // If no children, return just the checkbox
    if (!children) {
      return checkbox;
    }

    // If children provided, wrap in a label
    return (
      <div className="flex cursor-pointer items-start gap-2 text-sm">
        <div className="flex h-5 flex-row items-center">{checkbox}</div>
        <label
          htmlFor={checkboxId}
          className={cn(
            "cursor-pointer font-normal select-none",
            "peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
          )}
        >
          {children}
        </label>
      </div>
    );
  },
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
